import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import PropTypes from 'prop-types';
import useSessionContext from 'src/hooks/useSessionContext';

const RouteGuard = ({ children }) => {
  const { oktaAuth, authState, _onAuthRequired } = useOktaAuth();
  const location = useLocation();
  const pendingLogin = React.useRef(false);
  const [requestedLocation, setRequestedLocation] = React.useState(null);
  const { isIdle } = useSessionContext();
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleLogin = async () => {
      if (pendingLogin.current) {
        return;
      }

      pendingLogin.current = true;
      // oktaAuth.setOriginalUri();

      navigate(`/sso?redirect_url=${location.pathname}`);
      // await oktaAuth.signInWithRedirect();
    };

    if (authState.isAuthenticated) {
      pendingLogin.current = false;
      return;
    }

    // Start login if app has decided it is not logged in and there is no pending signin
    if (!authState.isAuthenticated) {
      if (!authState.isPending) {
        handleLogin();
      } else if (isIdle) {
        oktaAuth.tokenManager
          .getTokens()
          .then(async ({ accessToken, idToken }) => {
            if (
              (accessToken && oktaAuth.tokenManager.hasExpired(accessToken)) ||
              (idToken && oktaAuth.tokenManager.hasExpired(idToken)) ||
              !(await oktaAuth.isAuthenticated())
            ) {
              oktaAuth.token.getWithRedirect();
            }
          })
          .catch(() => {
            oktaAuth.token.getWithRedirect();
          });
      }
    }
  }, [
    authState.isPending,
    authState.isAuthenticated,
    oktaAuth,
    _onAuthRequired
  ]);

  if (!authState.isAuthenticated) {
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }
    return null;
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

RouteGuard.propTypes = {
  children: PropTypes.node.isRequired
};

export default RouteGuard;
